import { useIntl } from 'react-intl';
import { Col, Form, FormInstance, Input, Row } from 'antd';

import { useDefaultPercentageFieldError } from '@/hooks';

import { parseCurrency } from '@/utils/number';

import { CostItem } from '@/core/domain/stock/Configuration/Configuration.types';

import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { getPartRealCost } from '@/core/domain/stock/StockItems/StockItems.utils';
import { useEffect } from 'react';

export interface CompanyConsumableStockPricesFormData {
  stock: number;
  avaiableStock: number;
  reservedStock: number;
  minStock: number;
  maxStock: number;
  buyPrice: number;
  ipi: number;
  taxReplacement: number;
  fixedCost: number;
  operationalExpenses: number;
  freightCost: number;
  otherExpenses: number;
  realCost: number;
  location?: string;
}

interface CompanyConsumableStockPricesFormProps {
  form: FormInstance<CompanyConsumableStockPricesFormData>;
  costFields?: CostItem[];
}

function CompanyConsumableStockPricesForm({
  form,
  costFields,
}: CompanyConsumableStockPricesFormProps) {
  const intl = useIntl();
  const percentageError = useDefaultPercentageFieldError();

  const formValues = Form.useWatch([], form);

  const getConsumableExpenses = () => {
    const buyPrice = parseCurrency(form.getFieldValue('buyPrice'));
    const ipi = parseCurrency(form.getFieldValue('ipi'));
    const fixedCost = parseCurrency(form.getFieldValue('fixedCost'));
    const taxReplacement = parseCurrency(form.getFieldValue('taxReplacement'));
    const freightCost = parseCurrency(form.getFieldValue('freightCost'));
    const otherExpenses = parseCurrency(form.getFieldValue('otherExpenses'));
    const operationalExpenses = parseCurrency(
      form.getFieldValue('operationalExpenses'),
    );

    return {
      buyPrice,
      ipi,
      taxReplacement,
      fixedCost,
      freightCost,
      otherExpenses,
      operationalExpenses,
    };
  };

  const handlePriceChange = () => {
    const realCost = getPartRealCost(getConsumableExpenses(), costFields || []);
    form.setFieldsValue({ realCost });
  };

  useEffect(() => {
    handlePriceChange();
  }, [formValues]);

  return (
    <Form form={form}>
      <Row gutter={[16, 10]}>
        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="stock"
            label={intl.formatMessage({ id: 'consumables.stock.domain.stock' })}
            initialValue={0}
          >
            <InputDecimal disabled />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="minStock"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.minStock',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="maxStock"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.maxStock',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="buyPrice"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.buyPrice',
            })}
          >
            <InputDecimal fractions={4} />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="ipi"
            label={intl.formatMessage({ id: 'consumables.stock.domain.ipi' })}
            rules={[percentageError]}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="taxReplacement"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.taxReplacement',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="fixedCost"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.fixedCost',
            })}
          >
            <InputDecimal disabled fractions={4} />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="freightCost"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.freightCost',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="otherExpenses"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.otherExpenses',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={6}>
          <FloatingFormItem
            name="operationalExpenses"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.operationalExpenses',
            })}
          >
            <InputDecimal fractions={2} />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={12}>
          <FloatingFormItem
            name="realCost"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.realCost',
            })}
          >
            <InputDecimal disabled fractions={4} />
          </FloatingFormItem>
        </Col>

        <Col span={24} sm={12} lg={8} xl={12}>
          <FloatingFormItem
            name="location"
            label={intl.formatMessage({
              id: 'consumables.stock.domain.location',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default CompanyConsumableStockPricesForm;
